import React, { FunctionComponent } from 'react'
import { connect, useSelector } from 'react-redux'

import { bindActionCreators } from 'redux'

import { PATHWAYS_EVENTS, PATHWAYS_PAGE, PATHWAYS_PROPERTIES, PATHWAYS_TYPE } from '@lyrahealth-inc/shared-app-logic'
import { ComingSoonMicrositeHomePage } from '@lyrahealth-inc/ui-core-crossplatform/src/microsite'

import { useComingSoon } from '../../../../common/hooks/useComingSoon'
import { useCustomerCostProperties } from '../../../../common/hooks/useCustomerCostProperties'
import { useIsCostMessagingEnabled } from '../../../../common/hooks/useIsCostMessagingEnabled'
import { useMicrositeSectionProps } from '../../../../common/hooks/useMicrositeSectionProps'
import { useUpdatedMicrositeConfig } from '../../../../common/hooks/useUpdatedMicrositeConfig'
import { doesUserHaveMembershipInCustomer } from '../../../../common/utils/customerUtils'
import {
  getBeforeLaunch,
  getCustomerLaunchDate,
  getCustomerPropertiesMap,
  getProgramMembershipType,
} from '../../../../data/customer/customerSelectors'
import { trackEventWithObj } from '../../../../data/mixpanel'
import { TrackEventWithObjAction } from '../../../../data/mixpanel/types'
import { getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer } from '../../../../data/user/userSelectors'

interface ComingSoonMicrositeHomeContainerProps {
  actions: {
    trackEventWithObj: TrackEventWithObjAction
  }
}

const ComingSoonMicrositeHomeContainer: FunctionComponent<ComingSoonMicrositeHomeContainerProps> = ({
  actions: { trackEventWithObj },
}) => {
  const startDate = useSelector(getCustomerLaunchDate)
  const isUserAssumedInternational = useSelector(getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer)
  const membershipType = useSelector(getProgramMembershipType)
  const beforeLaunch = useSelector(getBeforeLaunch)
  const customerPropertiesMap = useSelector(getCustomerPropertiesMap)?.toJS()
  const hasMembershipInCustomer = doesUserHaveMembershipInCustomer({ membershipType })

  const updatedMicrositeConfig = useUpdatedMicrositeConfig(PATHWAYS_TYPE.COMING_SOON)

  const customerCostProperties = useCustomerCostProperties()
  const isCostMessagingEnabled = useIsCostMessagingEnabled()
  const { onNotifyMeOnLaunchPress } = useComingSoon()
  const {
    micrositeSectionProps: { selfCareSectionProps, legalAndCopyrightFooterProps, membershipSectionProps },
  } = useMicrositeSectionProps({ trackEventWithObj, isComingSoon: true })

  return (
    <ComingSoonMicrositeHomePage
      micrositeConfig={updatedMicrositeConfig}
      heroSectionProps={{
        pathwaysType: PATHWAYS_TYPE.COMING_SOON,
        startDate,
        onNotifyMeOnLaunchPress,
        onExploreOnYourOwnCTAPress: () => {
          document
            .querySelector('[data-testid="HeroSection-pathwaysHomepage"]')
            ?.nextElementSibling?.scrollIntoView({ behavior: 'smooth' })
          trackEventWithObj({
            event: PATHWAYS_EVENTS.CLICK_EXPLORE_ON_YOUR_OWN,
            page: PATHWAYS_PAGE.COMING_SOON,
          })
        },
      }}
      lyraOfferingsSectionProps={{
        pathwaysType: PATHWAYS_TYPE.COMING_SOON,
        customerPropertiesMap,
        isInternational: isUserAssumedInternational,
        meetWithAnExpertTabPress: () => {
          trackEventWithObj({
            event: PATHWAYS_EVENTS.VIEW_MEET_WITH_AN_EXPERT_TAB,
            page: PATHWAYS_PAGE.COMING_SOON,
          })
        },
        wellnessResourcesTabPress: () => {
          trackEventWithObj({
            event: PATHWAYS_EVENTS.VIEW_CONTENT_LIBRARY_TAB,
            page: PATHWAYS_PAGE.COMING_SOON,
          })
        },
        lyraOfferingsTabPress: () => {
          trackEventWithObj({
            event: PATHWAYS_EVENTS.VIEW_ALL_OFFERINGS_TAB,
            page: PATHWAYS_PAGE.COMING_SOON,
          })
        },
      }}
      selfCareSectionProps={selfCareSectionProps}
      careNavigatorSectionProps={{
        pathwaysType: PATHWAYS_TYPE.COMING_SOON,
      }}
      faqSectionProps={{
        membershipType,
        beforeLaunch,
        startDate,
        hasMembershipInCustomer,
        onNotifyMeOnLaunchPress,
        onQuestionPress: (title: string) => {
          trackEventWithObj({
            event: PATHWAYS_EVENTS.CLICK_FAQS,
            page: PATHWAYS_PAGE.COMING_SOON,
            [PATHWAYS_PROPERTIES.FAQ_QUESTION]: title,
          })
        },
        customerCostProperties,
        isCostMessagingEnabled,
      }}
      legalAndCopyrightFooterProps={legalAndCopyrightFooterProps}
      membershipSectionProps={membershipSectionProps}
      mobileAppPromoSectionProps={{ pathwaysType: PATHWAYS_TYPE.COMING_SOON }}
    />
  )
}

function mapDispatchToProps(dispatch: $TSFixMe) {
  return {
    actions: bindActionCreators({ trackEventWithObj }, dispatch),
  }
}

export default connect(null, mapDispatchToProps)(ComingSoonMicrositeHomeContainer)
